import React, { Component } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import styled from "styled-components";

import {
  Column,
  CPage,
  CButton,
  CFontIcon,
  H1,
  H3
} from "@appcues/component-library";

import { navigate } from "actions/routing";
import { callApi as fetchSegmentMembership } from "actions/account/segmentMemberships";
import {
  selectAccountSegments,
  selectAccountSegmentsSynced
} from "reducers/account/segments";
import {
  selectFlows,
  selectStepGroups,
  selectAreFlowsSynced
} from "reducers/account/flows";
import { selectUserRole } from "reducers/account/users";
import { selectUserId } from "reducers/user";
import {
  selectSegmentMembership,
  selectSegmentMembershipsSynced,
  selectSegmentSampleUserIds
} from "reducers/account/segmentMemberships";
import { ACCOUNT_PERMISSIONS } from "constants/accountManagement/permissions";
import segmentUtils from "utils/segments";

import CreatedUpdatedHeader from "components/Common/CreatedUpdatedHeader";
import UserSampleTable from "components/Common/UserSampleTable";
import ConditionsEstimatePanel from "./ConditionsEstimatePanel";
import FlowsUsingSegment from "./FlowsUsingSegment";
import UserCountPanel from "./UserCountPanel";
import DescriptionPanel from "./DescriptionPanel";
import ClausePanel from "./ClausePanel";

export class SegmentsView extends Component {
  static propTypes = {
    segmentId: PropTypes.string,
    segments: PropTypes.object,
    segmentMembership: PropTypes.shape({
      count: PropTypes.number,
      basis: PropTypes.number
    }),
    segmentUserSample: PropTypes.shape({
      meta: PropTypes.shape({
        fetching: PropTypes.bool,
        synced: PropTypes.bool,
        errored: PropTypes.bool
      }),
      data: PropTypes.arrayOf(
        PropTypes.shape({
          user_id: PropTypes.string,
          last_seen_timestamp: PropTypes.string
        })
      )
    }),
    loaded: PropTypes.bool,
    onNavigate: PropTypes.func,
    onLoad: PropTypes.func
  };

  componentDidMount() {
    this.props.onLoad();
  }

  switchToEdit = () => {
    const { segmentId, onNavigate } = this.props;
    onNavigate(`/segments/${segmentId}/edit`);
  };

  render() {
    const {
      segmentId,
      segments,
      steps,
      segmentMembership,
      segmentUserSample,
      role
    } = this.props;
    const segment = (segments || {})[segmentId];
    const description =
      typeof segment !== "undefined" ? segment.description : "";
    const isLoaded = this.props.loaded && segment && segmentId;

    let shouldUseEstimateData = false;
    if (isLoaded && segment) {
      shouldUseEstimateData = segmentUtils.shouldUseEstimateData(segment);
    }

    const canEditSegments = role !== ACCOUNT_PERMISSIONS.EDITOR;

    if (isLoaded) {
      return (
        <CPage title={segment.name} isLoading={!segment.conditions}>
          <CPage.TitleBar>
            <CPage.TitleBar.BreadcrumbLink href="/segments">
              Segments
            </CPage.TitleBar.BreadcrumbLink>
            <H1>{segment.name}</H1>
            <CPage.TitleBar.Actions>
              {canEditSegments && (
                <CButton type="primary" onClick={this.switchToEdit}>
                  <CFontIcon className="fas fa-edit" /> Edit
                </CButton>
              )}
            </CPage.TitleBar.Actions>
            <CreatedUpdatedHeader
              createdByUserId={segment.createdBy}
              createdAt={segment.createdAt}
              updatedByUserId={segment.updatedBy}
              updatedAt={segment.updatedAt}
            />
          </CPage.TitleBar>
          <CPage.Container>
            <Column>
              <DescriptionPanel description={description} />
              <ClausePanel
                conditions={segment.conditions}
                segments={segments}
                steps={steps}
              />
              {segment && shouldUseEstimateData ? (
                <ConditionsEstimatePanel conditions={segment.conditions} />
              ) : (
                <UserCountPanel
                  userCount={segmentMembership.count}
                  activeUsers={segmentMembership.basis}
                  percentOfAllUsers={
                    segmentMembership.count / segmentMembership.basis
                  }
                />
              )}
              {segment && !shouldUseEstimateData && (
                <UserSampleTable
                  title="25 most recent users"
                  userSample={segmentUserSample}
                />
              )}
              <H3 marginTop={48}>Flows using this segment</H3>
              <StyledFlowListContainer>
                <FlowsUsingSegment
                  segmentId={segmentId}
                  segmentName={segment.name}
                />
              </StyledFlowListContainer>
            </Column>
          </CPage.Container>
        </CPage>
      );
    }
    return <CPage title="" isLoading />;
  }
}

function mapStateToProps(state, routeProps) {
  const { segmentId } = routeProps.match.params;

  const segmentUsersDataLoaded = selectSegmentMembershipsSynced(state);

  const stepsLoaded = selectAreFlowsSynced(state);
  return {
    segmentId,
    segments: selectAccountSegments(state),
    role: selectUserRole(state, selectUserId(state)),
    steps: { ...selectStepGroups(state), ...selectFlows(state) },
    segmentMembership: selectSegmentMembership(state, { segmentId }),
    segmentUserSample: selectSegmentSampleUserIds(state, { segmentId }),
    loaded:
      selectAccountSegmentsSynced(state) &&
      stepsLoaded &&
      segmentUsersDataLoaded
  };
}

function mapDispatchToProps(
  dispatch,
  {
    match: {
      params: { segmentId }
    }
  }
) {
  return {
    onLoad: () => fetchSegmentMembership({ id: segmentId }),
    onNavigate: path => dispatch(navigate(path))
  };
}

// language=SCSS prefix=dummy{ suffix=}
const StyledFlowListContainer = styled.div`
  width: 100%;
  > div {
    margin-bottom: 16px;
  }
`;

export default connect(mapStateToProps, mapDispatchToProps)(SegmentsView);
